/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: react.js library.
import JoiBrowser from 'joi-browser' // Npm: Joi for frontend validation.
import _ from 'underscore' // Npm: Underscore.js library.
import { toast } from 'react-hot-toast' // Npm: React hot toast.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useLazyQuery, useMutation } from '@apollo/client' // Npm: Apollo client.
import { HiBolt } from 'react-icons/hi2' // Npm: React icons.
import {
  Button,
  Checkbox,
  Flex,
  Text,
  useBreakpointValue
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import SubmitButton from 'components/SubmitButton'
import MccMncSelector from 'components/MccMncSelector'
import VendorAccountSelector from 'components/VendorAccountSelector'
import SenderIdsDirectorySelector from 'components/SenderIdsDirectorySelector'
import VendorAccounttDirectorySelector from 'components/VendorAccounttDirectorySelector'
import { MemoizedInput, MemoizedSelect } from 'components/MemoizedInput'


/*
 * GRAPHS
 */
import SenderRoutePlanReadQuery from './__query__/index.senderroutePlan.read.query'
import SenderRoutePlanUpdateMutation from './__mutation__/index.senderroutePlan.update.mutation'
import SenderRoutePlanCreateMutation from './__mutation__/index.senderroutePlan.create.mutation'
import VendorAccountAttachSenderRoutePlanMutation from './__mutation__/index.vendorAccount.attachsenderRoutePlan.mutation'


/*
 * OBJECTS
 */
const Index = ({
  isOpen,
  isCreateOnly,
  onClose,
  passOn,
  refetch
}) => {
  // Hook assignment.
  const [vendorAccount, setVendorAccount] = React.useState(void 0)
  const [forms, setForms] = React.useState([1])
  const [MutationVendorAccountAttachSenderRoutePlan, MutationVendorAccountAttachSenderRoutePlanResponse] = useMutation(VendorAccountAttachSenderRoutePlanMutation)
  const [error, setError] = React.useState('')
  const [forceReRender, setForceReRender] = React.useState('')
  const [QuerySenderRoutePlanRead, QuerySenderRoutePlanReadResponse] = useLazyQuery(SenderRoutePlanReadQuery, { 'variables': { 'routePlanId': passOn?.routePlanId } })
  const [MutationSenderRoutePlanCreate, MutationSenderRoutePlanCreateResponse] = useMutation(SenderRoutePlanCreateMutation)
  const [MutationSenderRoutePlanUpdate, MutationSenderRoutePlanUpdateResponse] = useMutation(SenderRoutePlanUpdateMutation)
  const _formDataRef = React.useRef({})
  const _isCurrentViewMobile = useBreakpointValue({ 'base': 'false', 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  // Object assignment.
  const _AttachVendorAccountToSenderRoutePlan = async __id => {
    // Only Execute if route has data.
    if (!_.isEmpty(vendorAccount) && vendorAccount.includes('(') && vendorAccount.includes(')')) {
      // Const assignment.
      const vendorAccountId = vendorAccount.split(' ')[1].replace('(', '').replace(')', '')

      // Execute mutation.
      const _MutationVendorAccountAttachSenderRoutePlan = await MutationVendorAccountAttachSenderRoutePlan({
        'variables': {
          'routePlanId': __id,
          'vendorAccountId': vendorAccountId
        }
      })

      // If mutation caught an exception then report failure.
      if (_MutationVendorAccountAttachSenderRoutePlan instanceof Error) return _MutationVendorAccountAttachSenderRoutePlan

      // Style Guide.
      toast(_.every(_.pluck(_MutationVendorAccountAttachSenderRoutePlan?.data?.VendorAccountAttachSenderRoutePlan, 'status'), i => 'UPDATE_SUCCESSFUL' === i) ? 'Successfully attached the vendor with Sender route plan' : 'Something went wrong. Please try again after some time.')

      // On successful bind close modal.
      if (_.every(_.pluck(_MutationVendorAccountAttachSenderRoutePlan?.data?.VendorAccountAttachSenderRoutePlan, 'status'), i => 'UPDATE_SUCCESSFUL' === i)) onClose()
    }

    // Report void 0.
    return void 0
  }
  const _SubmitForm = async e => {
    // Local variable.
    let _routePlanIds

    // Const assignment.
    const _loadBalancerFilteredData = []

    // Prevent default behavior.
    e.preventDefault()

    // Reset error.
    setError('')

    // Const assignment.
    const _JoiSchema = JoiBrowser.object({
      'vendorAccount': JoiBrowser.string(),
      'routeToVendorAccount': JoiBrowser.string(),
      // 'smsType': ['RESELLER', 'RETAIL'].includes(passOn.type) ? JoiBrowser.string().optional() : JoiBrowser.string().optional(),
      'mcc': JoiBrowser.string().required(),
      'mnc': JoiBrowser.array().items(JoiBrowser.string().required()).required(),
      'senderIdDirectoryId': JoiBrowser.string(),
      'senderId': JoiBrowser.array().items(JoiBrowser.string()),
      'senderIdVendorAccountId': JoiBrowser.string(),
      'regex': JoiBrowser.array().items(JoiBrowser.string().optional())
    }).options({ 'allowUnknown': true })

    // Make sure that mnc is array.
    if (!isCreateOnly && !_.isArray(_formDataRef.current?.mnc)) _formDataRef.current = { ..._formDataRef.current, 'mnc': [(_formDataRef.current?.mnc)?.split('(')[1]?.split(')')[0]] }

    /*
     * Report failure if leastCostRoute is off and vendorAccount
     * priority is not set.
     */
    if (_.isEmpty(_formDataRef.current?.vendorAccount)) return setError('vendorAccount is required.')

    // Remove all keys from _formDataRef.current which are undefined.
    _formDataRef.current = { ..._.pick(_formDataRef.current, _.identity) }

    // Validate form data.
    const _JoiSchemaValidate = _JoiSchema.validate(_formDataRef.current)

    // If error exists then report failure.
    if (_JoiSchemaValidate.error) return setError(_JoiSchemaValidate.error?.message)
    // if (_.isEmpty(_formDataRef.current?.regex)) return setError('regex is required.')
    if (_.isEmpty(_formDataRef.current.regex) && _formDataRef.current.senderId && 1 <= _formDataRef.current.senderId.length && _.isEmpty(_formDataRef.current.senderIdVendorAccountId)) return setError('senderIdVendorAccountId is required if you are adding SenderId based routing.')

    /*
     * Update _formDataRef with routePlanId
     * if it doesn't exist and also make sure that mode
     * is not create only.
     */
    if (!isCreateOnly && _.isEmpty(_formDataRef.current?.routePlanId)) _formDataRef.current = { ..._formDataRef.current, 'routePlanId': passOn?.routePlanId }
    if (isCreateOnly && _.isEmpty(_formDataRef.current?.routeId)) _formDataRef.current = { ..._formDataRef.current, 'routeId': passOn?.routeId }

    /*
     * Update _formDataRef with input and senderRoutePlan
     * if it doesn't exist. Also make sure that mode is
     * not create only.
     */
    if (_.isEmpty(_formDataRef.current?.input)) _formDataRef.current = ({ 'input': [_formDataRef.current], ..._formDataRef.current })

    // Execute update mutation.
    const _MutationSenderRoutePlanUpdate = await [isCreateOnly ? MutationSenderRoutePlanCreate : MutationSenderRoutePlanUpdate]?.[0]({
      'variables': {
        'routePlanId': _.isEmpty(passOn?.routePlanIds) ? isCreateOnly ? '' : passOn?.routePlanId : void 0,
        'routePlanIds': _.isEmpty(passOn?.routePlanIds) ? void 0 : isCreateOnly ? '' : passOn?.routePlanIds,
        'input': _.pick(_formDataRef.current, 'input').input.map(j => {
          // Const assignment.
          let _mcc, _mnc, _senderIdDirectoryId, _senderIdVendorAccountId, _senderIds, _vendorAccountt, _vendorAccounttDirectory

          // Variable assignment.
          _senderIdDirectoryId = j?.senderIdDirectoryId
          _senderIds = j?.senderId
          _senderIdVendorAccountId = j?.senderIdVendorAccountId
          _vendorAccountt = j?.vendorAccountt
          _vendorAccounttDirectory = j?.vendorAccounttDirectory
          _mcc = j?.mcc
          _mnc = j?.mnc

          // Conditionally update senderId and senderIdDirectoryId.
          if (j?.senderIdDirectoryId && (j?.senderIdDirectoryId?.includes('(') || j?.senderIdDirectoryId?.includes(')'))) _senderIdDirectoryId = j?.senderIdDirectoryId.split('(')[1].split(')')[0]
          if (j?.senderId && 0 < j?.senderId?.length) _senderIds = _.compact(_.flatten(j?.senderId.map(r => r?.includes('(') && r?.includes(')') ? r.split('(')[1].split(')')[0] : r)))
          if (j?.senderIdVendorAccountId && (j?.senderIdVendorAccountId?.includes('(') || j?.senderIdVendorAccountId?.includes(')'))) _senderIdVendorAccountId = j?.senderIdVendorAccountId.split('(')[1].split(')')[0]
          if (j?.vendorAccountt && 0 < j?.vendorAccountt?.length) _vendorAccountt = _.compact(_.flatten(j?.vendorAccountt.map(r => r?.includes('(') && r?.includes(')') ? r.split('(')[1].split(')')[0] : r)))
          if (j?.vendorAccounttDirectory && (j?.vendorAccounttDirectory?.includes('(') || j?.vendorAccounttDirectory?.includes(')'))) _vendorAccounttDirectory = j?.vendorAccounttDirectory.split('(')[1].split(')')[0]
          if (j?.mcc && (j?.mcc?.includes('(') || j?.mcc?.includes(')'))) _mcc = j.mcc.split('(')[1].split(')')[0]
          if (j?.mnc && 0 < j?.mnc?.length) _mnc = _.compact(_.flatten(j?.mnc.map(r => r?.includes('(') && r?.includes(')') ? r.split('(')[1].split(')')[0] : r)))

          // Return data.
          return ({
            'mcc': _mcc,
            'mnc': _mnc,
            'regex': j?.regex,
            'senderIdDirectoryId': !_.isEmpty(j?.regex) ? void 0 : _senderIdDirectoryId,
            'senderId': !_.isEmpty(j?.regex) ? void 0 : _senderIds,
            'senderIdVendorAccountId': !_.isEmpty(j?.regex) ? void 0 : _.isEmpty(_senderIds) ? void 0 : _senderIdVendorAccountId,
            'vendorAccountt': void 0,
            'vendorAccounttDirectory': void 0,
            ...(isCreateOnly ? { 'routeId': passOn?.routeId } : {})
          })
        })
      }
    })

    // If mutation caught an exception then report failure.
    if (_MutationSenderRoutePlanUpdate instanceof Error) return _MutationSenderRoutePlanUpdate

    // If Update contains data then update routePlanIds.
    if (!_.isEmpty(_MutationSenderRoutePlanUpdate?.data?.SenderRoutePlanUpdate)) _routePlanIds = _.compact(_.pluck(_MutationSenderRoutePlanUpdate?.data?.SenderRoutePlanUpdate, 'id'))
    if (!_.isEmpty(_MutationSenderRoutePlanUpdate?.data?.SenderRoutePlanCreate)) _routePlanIds = _.compact(_.pluck(_MutationSenderRoutePlanUpdate?.data?.SenderRoutePlanCreate, 'id'))

    // Attach vendor account and route to customer.
    !_.isEmpty(_routePlanIds) && _.isArray(_routePlanIds) && await _AttachVendorAccountToSenderRoutePlan(_routePlanIds)

    // Check if create was successful.
    if (!_.isEmpty(_MutationSenderRoutePlanUpdate?.data?.SenderRoutePlanCreate) && _.every(_.pluck(_MutationSenderRoutePlanUpdate?.data?.SenderRoutePlanCreate, 'status') ?? [], j => 'CREATE_SUCCESSFUL' === j)) {
      // Style guide.
      toast('Successfully upserted the Sender route plan.')

      // On successful create close modal.
      refetch?.()
      return onClose?.()
    }
    if (!_.isEmpty(_MutationSenderRoutePlanUpdate?.data?.SenderRoutePlanUpdate) && _.every(_.pluck(_MutationSenderRoutePlanUpdate?.data?.SenderRoutePlanUpdate, 'status') ?? [], j => 'UPDATE_SUCCESSFUL' === j)) {
      // Style guide.
      toast('Successfully upserted the Sender route plan.')

      // Report failure.
      refetch?.()
      return onClose?.()
    }

    // Style Guide.
    toast((_.isEmpty(_MutationSenderRoutePlanUpdate?.data?.SenderRoutePlanCreate) ? [0 === _.pluck(_MutationSenderRoutePlanUpdate?.data?.SenderRoutePlanUpdate, 'message')?.length ? 'Same plan already exists. kindly delete it or modify it instead.' : _.pluck(_MutationSenderRoutePlanUpdate?.data?.SenderRoutePlanUpdate, 'message')]?.join(' ') : _.pluck(_MutationSenderRoutePlanUpdate?.data?.SenderRoutePlanCreate, 'message')?.join(' ')))

    // Report void.
    return void 0
  }

  // Event handler.
  React.useEffect(() => {
    // _Async handler.
    const _Async = async () => {
      // Const assignment.
      const _QuerySenderRoutePlanReadQuery = await QuerySenderRoutePlanRead({ 'variables': { 'routePlanId': isCreateOnly ? 'UN_KNOWN' : passOn?.routePlanId } })

      // If query caught an exception then report failure.
      if (_QuerySenderRoutePlanReadQuery instanceof Error) return _QuerySenderRoutePlanReadQuery

      /*
       * If senderRoutePlan details fetch complete then
       * update its value.
       */

      if (_.first(_QuerySenderRoutePlanReadQuery?.data?.SenderRoutePlanRead)?.status === "READ_SUCCESSFUL") {
        // Const assignment.
        const _senderRoutePlanRead = _.first(_QuerySenderRoutePlanReadQuery?.data?.SenderRoutePlanRead)

        // Update form data.
        _formDataRef.current = {
          'routePlanId': passOn?.routePlanId,
          'createdAt': _senderRoutePlanRead?.createdAt,
          'updatedAt': _senderRoutePlanRead?.updatedAt,
          'mcc': _senderRoutePlanRead?.Mcc?.mcc ? `${_senderRoutePlanRead?.Mcc?.countryName} (${_senderRoutePlanRead?.Mcc?.mcc})` : void 0,
          'mnc': _senderRoutePlanRead?.Mnc?.mnc ? `(${_senderRoutePlanRead?.Mnc?.mnc}) ${_senderRoutePlanRead?.Mnc?.network}` : void 0,
          'senderId': _.isEmpty(_senderRoutePlanRead?.SenderId) ? void 0 : _senderRoutePlanRead?.SenderId?.map?.(j => `${j?.senderId} (${j?.id})`),
          'senderIdDirectoryId': _senderRoutePlanRead?.SenderIdDirectory?.displayName ? `${_senderRoutePlanRead?.SenderIdDirectory?.displayName} (${_senderRoutePlanRead?.SenderIdDirectory?.id})` : void 0,
          'countryName': _senderRoutePlanRead?.Mcc?.countryName,
          'senderIdVendorAccountId': _senderRoutePlanRead?.SenderIdVendorAccount?.displayName ? `${_senderRoutePlanRead?.SenderIdVendorAccount?.displayName} (${_senderRoutePlanRead?.SenderIdVendorAccount?.id})` : _senderRoutePlanRead?.VendorAccount?.displayName ? `${_senderRoutePlanRead?.VendorAccount?.displayName} (${_senderRoutePlanRead?.VendorAccount?.id})` : void 0,
          'regex': _senderRoutePlanRead?.regex,
          'vendorAccount': vendorAccount ?? 0 < _QuerySenderRoutePlanReadQuery?.data?.SenderRoutePlanRead?.length ? _senderRoutePlanRead?.VendorAccount?.displayName ? `${_senderRoutePlanRead?.VendorAccount?.displayName} (${_senderRoutePlanRead?.VendorAccount?.id})` : void 0 : void 0,
          'vendorAccounttDirectory': _senderRoutePlanRead?.VendorAccounttDirectory?.displayName ? `${_senderRoutePlanRead?.VendorAccounttDirectory?.displayName} (${_senderRoutePlanRead?.VendorAccounttDirectory?.id})` : void 0,
          'vendorAccountt': _.isEmpty(_senderRoutePlanRead?.VendorAccountt) ? void 0 : _senderRoutePlanRead?.VendorAccountt?.map?.(j => j?.displayName ? `${j?.displayName} (${j?.id})` : void 0)
        }

        // Update state.
        return setForceReRender(String.random(13))
      }

      // Report failure.
      return void 0
    }; _Async()
  }, [passOn, isOpen])

  // Const assignment.
  const _isLoading = MutationVendorAccountAttachSenderRoutePlanResponse.loading || MutationSenderRoutePlanUpdateResponse.loading || MutationSenderRoutePlanCreateResponse.loading
  const _isInputDisabled = isCreateOnly ? false : _isLoading || QuerySenderRoutePlanReadResponse.loading

  // Return component.
  return (
    <form onSubmit={_SubmitForm} className='senderRoutePlanUpsert'>
      <Flex gap='22px' flexDir='column' wrap='nowrap' w='100%'>
        <Flex width='100%' gap='18px' direction={{ 'base': 'column', 'md': 'row' }}>
          <VendorAccountSelector
            disabled={_isInputDisabled}
            name='vendorAccount'
            label='Vendor Account'
            placeholder='e.g. "Awesome Vendor Account"'
            value={_formDataRef?.current?.vendorAccount}
            isRequired={true}
            error={error}
            height='40px'
            isInvalid={error?.includes('vendorAccount') && !error?.includes('position')}
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: value,
                'senderIdVendorAccountId': value
              }

              // Update vendorAccount.
              setVendorAccount(value)
            }}
          />
          <MemoizedInput
            disabled={_isInputDisabled}
            name='regex'
            label='Regex Pattern'
            placeholder='e.g. "enter the value"'
            value={_formDataRef?.current?.regex}
            error={error}
            isMultiple={true}
            separators={[',', 'Enter', 'Tab', ' ']}
            isInvalid={error?.includes('regex')}
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef.current,
                [name]: value
              }
            }}
          />


        </Flex>
        <Flex width='100%' gap='22px' direction={{ 'base': 'column', 'md': 'row' }}>
          <MccMncSelector
            isRequired={true}
            disabled={_isInputDisabled}
            mccValue={_formDataRef?.current?.mcc}
            mncValue={_formDataRef?.current?.mnc}
            inValidMnc={error?.includes('mnc')}
            inValidMcc={error?.includes('mcc')}
            isMccAndMncGlobal={false}
            onChange={i => {
              // Update form data.
              _formDataRef.current = ({ ..._formDataRef.current, 'mnc': i.mnc, 'mcc': i.mcc })

              // Force re-render.
              setForceReRender(String.random(5))
            }}
          />
          <SenderIdsDirectorySelector
            disabled={_isInputDisabled}
            senderIdDirectoryValue={_formDataRef?.current?.senderIdDirectoryId ?? ''}
            senderIdValue={_formDataRef?.current?.senderId}
            inValidSenderId={error?.includes('senderId')}
            inValidSenderIdDirectory={error?.includes('senderIdDirectoryId')}
            onChange={i => {
              // Update form data.
              _formDataRef.current = ({ ..._formDataRef.current, 'senderId': i.senderId, 'senderIdDirectoryId': i.senderIdDirectory })

              // Force re-render.
              setForceReRender(String.random(6))
            }}
          />
        </Flex>

      </Flex>
      <Flex
        mt='22px'
        alignItems={_isCurrentViewMobile ? 'flex-end' : 'center'}
        justifyContent='space-between'
        flexDir='row'>
        <SubmitButton
          mt={0}
          disabled={_isInputDisabled}
          onSubmit={_SubmitForm}
          defaultText={isCreateOnly ? 'Create Sender Route Plan' : 'Update Sender Route Plan'}
          isLoading={_isLoading} />
      </Flex>
    </form>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'isCreateOnly': PropTypes.bool,
  'isOpen': PropTypes.bool,
  'onClose': PropTypes.func,
  'refetch': PropTypes.func,
  'passOn': PropTypes.object,
  'submitRef': PropTypes.object,
  'disabled': PropTypes.bool,
  'isUpsertMixerContext': PropTypes.bool,
  'setSubmitIsLoading': PropTypes.func
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
